import { markRaw } from "vue";
import { receiveChatStream } from "@/utils/chat_stream";
import type { HelpCenterChatResponse } from "@/api";
import type { ActionContext, Module } from "vuex";

import type { DefaultApi } from "@/api";
import { scoreEntries, searchIndex } from "@/data/help-center/search-index";
import type { State as RootState } from "@/stores";

export interface HelpCenterChatSource {
  slug: string;
  title: string;
}

export interface HelpCenterChatConversationMessage {
  message: string;
  isHuman: boolean;
  sources?: HelpCenterChatSource[];
}

export type HelpCenterChatErrorState = "none" | "rate_limited" | "failed";

export interface State {
  isOpen: boolean;
  messages: HelpCenterChatConversationMessage[];
  pending: boolean;
  error: HelpCenterChatErrorState;
  draft?: string;
  controller?: AbortController;
}

// Sending the full corpus on every request doesn't scale as the Help Center grows toward
// ~55 pages; 16 keeps the request token budget bounded regardless of corpus size.
export const HELP_CENTER_CHAT_TOP_K = 16;

// Recent-enough to follow a topic shift within the conversation without diluting token scores
// with an unrelated earlier thread.
const QUERY_HISTORY_TURNS = 3;

const buildQuery = (messages: HelpCenterChatConversationMessage[]): string =>
  messages
    .filter((message) => message.isHuman)
    .slice(-QUERY_HISTORY_TURNS)
    .map((message) => message.message)
    .join(" ");

// No padding: a query matching fewer than 16 pages (including zero) sends only what matched.
const buildPassages = (messages: HelpCenterChatConversationMessage[]) =>
  scoreEntries(buildQuery(messages))
    .slice(0, HELP_CENTER_CHAT_TOP_K)
    .map(({ entry }) => ({
      slug: entry.slug,
      title: entry.title,
      section_label: entry.sectionLabel,
      body: entry.body,
    }));

const resolveSources = (
  slugs: string[],
  sentSlugs: ReadonlySet<string>,
): HelpCenterChatSource[] =>
  slugs
    .filter((slug) => sentSlugs.has(slug))
    .map((slug) => searchIndex.find((entry) => entry.slug === slug))
    .filter(
      (entry): entry is (typeof searchIndex)[number] => entry !== undefined,
    )
    .map((entry) => ({ slug: entry.slug, title: entry.title }));

const errorStateFor = (error: unknown): HelpCenterChatErrorState => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 429 ? "rate_limited" : "failed";
};

// Caps what is sent to the server, not `state.messages` itself, so the panel keeps showing the
// full conversation while the request body stays well under the server's 40-message limit.
const MAX_HISTORY_TURNS = 20;

// Matches `HelpCenterChatMessage.message`'s max_length in server/src/phone_screening/models/help_center_chat.py.
export const MAX_MESSAGE_CHARS = 4000;

// A plain function, not a Vuex action, so `ask` and `retry` can call it directly without a second dispatch.
const sendConversation = async ({
  commit,
  dispatch,
  state,
}: ActionContext<State, RootState>): Promise<void> => {
  commit("setPending", true);
  commit("setError", "none");

  const controller = markRaw(new AbortController());
  commit("setController", controller);
  try {
    const api: DefaultApi = await dispatch("getAuthorizedApi", null, {
      root: true,
    });
    const passages = buildPassages(state.messages);
    const sentSlugs = new Set(passages.map((passage) => passage.slug));
    const data = await receiveChatStream<HelpCenterChatResponse>(
      (options) =>
        api.postHelpCenterChat(
          {
            // Clamp on replay: an unbounded assistant reply over the server's per-message max_length would 422
            // and wedge the conversation, since retry resends the same history.
            messages: state.messages.slice(-MAX_HISTORY_TURNS).map((entry) => ({
              message: entry.message.slice(0, MAX_MESSAGE_CHARS),
              is_human: entry.isHuman,
            })),
            passages,
          },
          options,
        ),
      { signal: controller.signal, onText: (text) => commit("setDraft", text) },
    );
    commit("appendMessage", {
      message: data.message,
      isHuman: false,
      sources: resolveSources(data.source_slugs, sentSlugs),
    });
  } catch (error) {
    if (!controller.signal.aborted) commit("setError", errorStateFor(error));
  } finally {
    commit("setDraft", "");
    commit("setController", undefined);
    commit("setPending", false);
  }
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: () => ({
    isOpen: false,
    messages: [],
    pending: false,
    error: "none",
  }),
  actions: {
    open({ commit }) {
      commit("setOpen", true);
    },
    close({ commit, state }) {
      state.controller?.abort();
      commit("setOpen", false);
    },
    toggle({ commit, state }) {
      if (state.isOpen) state.controller?.abort();
      commit("setOpen", !state.isOpen);
    },
    async ask(context, message: string) {
      if (context.state.pending) return;
      // The panel disables submission past this length; this is a backstop against any other caller.
      if (message.length > MAX_MESSAGE_CHARS) return;
      context.commit("appendMessage", { message, isHuman: true });
      await sendConversation(context);
    },
    async retry(context) {
      if (context.state.pending) return;
      await sendConversation(context);
    },
  },
  mutations: {
    setDraft(state, text: string) {
      state.draft = text;
    },
    setController(state, controller?: AbortController) {
      state.controller = controller;
    },
    setOpen(state, isOpen: boolean) {
      state.isOpen = isOpen;
    },
    appendMessage(state, message: HelpCenterChatConversationMessage) {
      state.messages = [...state.messages, message];
    },
    setPending(state, pending: boolean) {
      state.pending = pending;
    },
    setError(state, error: HelpCenterChatErrorState) {
      state.error = error;
    },
  },
};
